<script setup lang="ts">
/**
 * The desktop app's own frame: the chat, filling the window.
 *
 * Separate from `MainLayout` on purpose. That one is a website — a centred
 * column under a brand mark, a search box and two rows of navigation, sized so
 * a page of reading does not run the width of a monitor. A chat app is the
 * opposite: it is a tool that lives open all day, it is judged by how much
 * conversation fits, and every pixel of chrome is a pixel not showing messages.
 * Carrying both shapes in one file would have meant a `v-if` on nearly every
 * line of it.
 *
 * **No brand mark.** A logo earns its place on a website, where it says whose
 * site you are on; in an app it is already on the dock icon, in the window
 * title and in the menu bar, and the fourth copy costs the width of a name in
 * the one strip of chrome there is.
 *
 * What the strip is actually for: somewhere to grab the window — the shell
 * hides the title bar so the app reaches the top of the glass — and a way to
 * reach settings and sign out, which exist nowhere else once the navigation is
 * gone.
 *
 * The chat itself is the same `Messages` view the website renders. That is the
 * whole point: there is one transcript in this product, and it is not here.
 */
import { onMounted, onUnmounted, ref } from 'vue';
import { RouterLink, useRouter } from 'vue-router';

import Avatar from '../components/Avatar.vue';
import { isDesktopMac } from '../utils/desktop-shell.ts';
import { useAuthStore } from '../stores/auth.ts';
import { useChatStore } from '../stores/chat.ts';
import { usePresenceStore } from '../stores/presence.ts';
import { useRealtimeStore } from '../stores/realtime.ts';
import { handle } from '../utils/format.ts';
import { useI18n } from '../i18n/index.ts';

const auth = useAuthStore();
const chat = useChatStore();
const presence = usePresenceStore();
const realtime = useRealtimeStore();
const router = useRouter();
const { t } = useI18n();

const menuOpen = ref(false);

/**
 * Apple's three window buttons sit over the top-left corner when the title bar
 * is hidden, and nothing else knows they are there.
 */
const macInset = isDesktopMac();

function closeMenu(event: MouseEvent): void {
  const target = event.target as HTMLElement | null;
  if (!target?.closest('[data-account-menu]')) menuOpen.value = false;
}

/**
 * The socket, and only the socket.
 *
 * This is the whole reason a desktop app exists for this product: it stays
 * connected while the window is closed, so a message — including a group
 * message, which Web Push deliberately skips — reaches somebody who is not
 * looking at a screen. That arrives over this socket, and the notification the
 * operating system draws for it is unaffected by anything below.
 *
 * The website's shell also polls the notification *summary*, for the number on
 * the bell. There is no bell here, so there is nothing to poll for: asking the
 * server every few seconds for a count nobody is shown is work this app has no
 * reason to do.
 */
function connect(): void {
  realtime.start();
}

async function signOut(): Promise<void> {
  menuOpen.value = false;
  // The socket is what reports this account as online; it closes before the
  // session does.
  realtime.stop();
  presence.reset();
  await auth.logout();
  await router.push({ name: 'login' });
}

onMounted(() => {
  document.addEventListener('click', closeMenu);
  if (auth.isAuthenticated) connect();
});

onUnmounted(() => {
  document.removeEventListener('click', closeMenu);
  realtime.stop();
});
</script>

<template>
  <div class="flex h-dvh min-h-0 flex-col overflow-hidden bg-surface">
    <!--
      The only chrome there is: a handle for the window and a way to sign out.
      `tv-drag` makes the strip itself draggable and every control inside opts
      back out, or it would be a handle rather than a button.
    -->
    <header
      class="tv-drag flex h-11 shrink-0 items-center justify-end gap-1 border-b border-outline-variant/30 bg-surface-container-lowest px-2"
      :class="macInset ? 'pl-[5.5rem]' : ''"
    >
      <template v-if="auth.isAuthenticated && auth.user">
        <div class="tv-no-drag relative" data-account-menu>
          <button
            type="button"
            class="flex items-center gap-1 rounded-full p-0.5 transition-colors hover:bg-surface-container-low"
            :aria-expanded="menuOpen"
            :aria-label="t('nav.accountMenu')"
            aria-haspopup="menu"
            @click="menuOpen = !menuOpen"
          >
            <Avatar
              :name="auth.user.displayName"
              :src="auth.user.avatarUrl"
              :asset="auth.user.avatarAsset"
              :user-id="auth.user.id"
              size="xs"
            />
          </button>

          <div
            v-if="menuOpen"
            class="absolute right-0 z-40 mt-2 w-60 overflow-hidden rounded-xl bg-surface-container-lowest p-1 shadow-xl ring-1 ring-outline-variant/40"
            role="menu"
          >
            <span class="block px-3 py-2">
              <span class="block truncate text-label-md text-on-surface">
                {{ auth.user.displayName }}
              </span>
              <span class="block truncate text-body-sm text-on-surface-variant">
                {{ handle(auth.user.username) }}
              </span>
            </span>
            <hr class="my-1 border-outline-variant/40" />
            <RouterLink
              to="/settings"
              class="block rounded-lg px-3 py-2 text-body-md hover:bg-surface-container-low"
              role="menuitem"
              @click="menuOpen = false"
            >
              {{ t('nav.settingsPrivacy') }}
            </RouterLink>
            <button
              type="button"
              class="block w-full rounded-xl px-3 py-2 text-left text-body-md text-error hover:bg-error-container/40"
              role="menuitem"
              @click="signOut"
            >
              {{ t('nav.signOut') }}
            </button>
          </div>
        </div>
      </template>
    </header>

    <!--
      Everything else is the conversation. No container, no measure, no padding:
      the window was opened to hold a chat, so the chat is what fills it.
      `min-h-0` because a flex child will not shrink below its content without
      it, and the transcript inside scrolls itself.
    -->
    <main id="main" class="min-h-0 flex-1 overflow-hidden">
      <slot />
    </main>

    <!-- Unread total, as a page title: the shell reads it for the dock badge. -->
    <span class="sr-only" data-unread-total>{{ chat.totalUnread }}</span>
  </div>
</template>
